import React from 'react';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const StyledPremiumButton = styled(Button, {
  shouldForwardProp: (prop) => !['isPremium', 'disabled'].includes(prop),
})(({ isPremium, disabled }) => ({
  fontFamily: 'Playfair Display, serif',
  fontWeight: 500,
  fontSize: '1.2rem',
  letterSpacing: '0.09em',
  textTransform: 'none',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  color: isPremium ? '#d48aae' : '#7a6070',
  background: isPremium
    ? `radial-gradient(ellipse at 50% 0%, rgba(160, 55, 120, 0.18), transparent 65%),
       linear-gradient(to bottom, #2e0f20 0%, #1e0916 55%, #160710 100%)`
    : 'linear-gradient(to bottom, #1a0e18 0%, #120910 100%)',
  border: isPremium
    ? '1px solid rgba(160, 55, 110, 0.28)'
    : '1px solid rgba(140, 80, 110, 0.12)',
  boxShadow: isPremium
    ? '0 0 18px rgba(140, 40, 95, 0.18), 0 4px 14px rgba(0, 0, 0, 0.55)'
    : '0 2px 10px rgba(0, 0, 0, 0.4)',

  borderRadius: '50px',
  padding: '0',
  lineHeight: 1.3,
  width: '100%',
  margin: '5px',
  cursor: isPremium ? 'pointer' : 'default',
  textAlign: 'center',
  position: 'relative',
  filter: disabled ? 'grayscale(30%) brightness(0.8)' : 'none',
  opacity: disabled ? 0.7 : (isPremium ? 1 : 0.55),
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',

  '&:hover': {
    transform: isPremium && !disabled ? 'scale(1.03)' : 'none',
    boxShadow: isPremium && !disabled
      ? '0 0 24px rgba(155, 50, 110, 0.28), 0 4px 16px rgba(0, 0, 0, 0.6)'
      : undefined,
  },

  '.btn-icon': {
    position: 'absolute',
    right: 15,
    opacity: 0.8,
    fontSize: '1rem',
    pointerEvents: 'none',
  },

  '.btn-text': {
    flex: 1,
    textAlign: 'center',
    padding: '13px 20px',
  },

  '.btn-question': {
    position: 'absolute',
    right: 6,
    top: 6,
    fontSize: '0.9rem',
    cursor: 'pointer',
    opacity: 0.7,
    pointerEvents: 'auto',
    color: isPremium ? '#d48aae' : '#887080',
    transition: 'opacity 0.2s ease',
    '&:hover': {
      opacity: 1,
    },
    '& svg': {
      fontSize: '1rem',
    },
  },
}));

const PremiumButton = ({
  children,
  onClick,
  isPremium = false,
  icon = null,
  sx = {},
  style = {},
  showQuestionMark = false,
  onQuestionClick = null,
  ...rest
}) => {
  return (
    <StyledPremiumButton
      onClick={onClick}
      isPremium={isPremium}
      sx={sx}
      style={style}
      {...rest}
    >
      <span className="btn-text">{children}</span>
      {icon && <span className="btn-icon">{icon}</span>}
      {showQuestionMark && (
        <span
          className="btn-question"
          onClick={(e) => {
            e.stopPropagation(); // prevent triggering main click
            if (onQuestionClick) onQuestionClick();
          }}
        >
          <HelpOutlineIcon fontSize="small" />
        </span>
      )}
    </StyledPremiumButton>
  );
};

export default PremiumButton;
